import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, Typography, Link, Alert, Tabs, Tab, CircularProgress, Divider } from '@mui/material';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import coolyLogo from '../../assets/coolyLogo.png'; // Import the logo
import { FcGoogle } from 'react-icons/fc';
import { FaMicrosoft } from 'react-icons/fa';

const LoginForm = ({
  onLoginSuccess, 
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [activeTab, setActiveTab] = useState('signin'); 
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false); // Email form collapsed by default
  const [error, setError] = useState('');
  const [message, setMessage] = useState(''); 
  const [loading, setLoading] = useState(false);
  const auth = getAuth();

  const purpleColors = {
    main: '#8A2BE2', // A generic purple, adjust to your specific purple-500/600
    light: '#9370DB', // Lighter purple (purple-400)
    dark: '#6A1B9A',  // Darker purple (purple-700)
    contrastText: '#FFFFFF',
    borderLight: 'rgba(138, 43, 226, 0.3)', // purple-500/30
    borderFocused: 'rgba(138, 43, 226, 0.5)',
  };

  const textFieldStyles = {
    '& .MuiInputBase-root': {
      backgroundColor: '#fff', // Make input background white
      color: '#222',           // Make input text dark for contrast
      '& fieldset': {
        borderColor: purpleColors.borderLight,
      },
      '&:hover fieldset': {
        borderColor: purpleColors.borderFocused,
      },
      '&.Mui-focused fieldset': {
        borderColor: purpleColors.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'grey.700', // Make label darker for white background
      '&.Mui-focused': {
        color: purpleColors.light,
      },
    },
    '& .MuiFormHelperText-root': {
      color: 'grey.500',
    },
  };

  const buttonStyles = {
    backgroundColor: purpleColors.main, // bg-purple-600
    color: purpleColors.contrastText,
    '&:hover': {
      backgroundColor: purpleColors.dark, // hover:bg-purple-700
    },
    '&.Mui-disabled': {
      backgroundColor: 'grey.700',
      color: 'grey.500'
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setMessage('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      setMessage('');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error("Error during email sign in:", err.code, err.message);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many unsuccessful login attempts. Please try again later or reset your password.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This user account has been disabled.');
      } else {
        setError('Failed to sign in. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      setMessage('');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setMessage('');
      return;
    }
    if (password.length < 6) {
      setError('Password should be at least 6 characters long.');
      setMessage('');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error("Error during email sign up:", err.code, err.message);
      if (err.code === 'auth/email-already-in-use') {
        setError('Email already in use. Please sign in or use a different email.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else {
        setError('Failed to create account. Please try again.');
      }
      setLoading(false);
    }
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      setMessage('');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent. Please check your email for instructions.");
      setEmail(''); // Clear email field after successful send
    } catch (err) {
      console.error("Error sending password reset email:", err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (providerName) => {
    setError('');
    setLoading(true);
    console.log('LoginForm: Attempting OAuth sign-in with provider:', providerName);
    
    try {
      let provider;
      
      switch (providerName) {
        case 'google':
          provider = new GoogleAuthProvider();
          break;
        case 'microsoft':
          provider = new OAuthProvider('microsoft.com');
          provider.setCustomParameters({
            tenant: 'common'
          });
          break;
        default:
          throw new Error('Invalid provider');
      }

      const popupTimeout = setTimeout(() => {
        setLoading(false);
      }, 3000);

      await signInWithPopup(auth, provider).finally(() => {
        clearTimeout(popupTimeout);
      });
      
      // If we get here, signin was successful
      // App.js will handle auth state changes via onAuthStateChanged

    } catch (error) {
      console.error(`Error signing in with ${providerName}:`, error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in window was closed. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError(`Your browser blocked the ${providerName} sign-in popup. Please allow popups for this site and try again.`);
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Multiple popup requests were made. Please try again more slowly.');
      } else {
        setError(`Failed to sign in with ${providerName}: ${error.message}`);
      }
      
      setLoading(false);
    }
  };

  const SignInButton = ({ provider, icon, text }) => (
    <Button
      onClick={() => handleSignIn(provider)}
      disabled={loading}
      variant="outlined"
      fullWidth
      sx={{
        mb: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        backgroundColor: '#2A2A3E',
        color: 'white',
        border: `1px solid ${purpleColors.borderLight}`,
        '&:hover': {
          backgroundColor: 'rgba(138, 43, 226, 0.1)',
          borderColor: purpleColors.borderLight
        }
      }}
    >
      {loading && (provider === 'google' || provider === 'microsoft') ? 
        <CircularProgress size={20} color="inherit" /> : 
        icon
      }
      <span>
        {loading && (provider === 'google' || provider === 'microsoft') ? 
          `Signing in with ${provider}...` : text}
      </span>
    </Button>
  );

  const errorStyles = {
    container: {
      mt: 2, 
      mb: 1, 
      bgcolor: 'rgba(255,0,0,0.15)', // Slightly increased opacity
      border: '1px solid rgba(255,50,50,0.5)', // Added border for more visibility
      borderRadius: '4px',
      padding: '10px 16px',
    },
    text: {
      color: '#ff9999', // Much lighter red for better contrast on dark background
      fontWeight: '500', // Medium font weight for better visibility
    }
  };

  if (showResetPassword) {
    return (
      <Box 
        component="form" 
        onSubmit={handlePasswordResetSubmit} 
        sx={{ 
          mt: 1, 
          p: { xs: 2, sm: 3 },
          bgcolor: '#2A2A3E', // Card background
          borderRadius: 2, 
          border: `1px solid ${purpleColors.borderLight}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          color: 'grey.300', // text-gray-300
          maxWidth: 400,
          mx: 'auto',
        }}
      >
        <Typography variant="h6" gutterBottom align="center" sx={{ color: purpleColors.light, mb: 2 }}>
          Reset Password
        </Typography>
        {error && (
          <Box sx={errorStyles.container}>
            <Typography variant="body2" sx={errorStyles.text}>
              {error}
            </Typography>
          </Box>
        )}
        {message && <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgba(0,255,0,0.1)', color: 'green.300' }}>{message}</Alert>}
        <TextField
          margin="normal"
          required
          fullWidth
          id="reset-email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          sx={textFieldStyles}
        />
        <Button type="submit" fullWidth variant="contained" sx={{ ...buttonStyles, mt: 3, mb: 1 }} disabled={loading}>
          {loading ? <CircularProgress size={24} sx={{ color: purpleColors.contrastText }}/> : 'Send Reset Link'}
        </Button>
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => { setShowResetPassword(false); setError(''); setMessage(''); setEmail(''); setPassword(''); }} 
          sx={{ display: 'block', textAlign: 'center', mt:1, color: purpleColors.light, '&:hover': { textDecoration: 'underline' } }}
        >
          Back to Sign In
        </Link>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%', 
        maxWidth: 400, 
        margin: 'auto', 
        bgcolor: '#2A2A3E', // bg-[#2A2A3E]
        p: {xs: 2, sm: 3}, 
        borderRadius: 2, // rounded-lg
        border: `1px solid ${purpleColors.borderLight}`, // border border-purple-500/20
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)', // A subtle shadow
        color: 'grey.300' // Default text color for the box (text-gray-300)
      }}
    >
      <Typography variant="h5" gutterBottom align="center" sx={{ color: purpleColors.light, fontWeight: 'bold' }}>
        Welcome to Coooly
      </Typography>
      <Typography variant="body2" color="grey.400" align="center" sx={{ mb: 2 }}>
        Get started with smart invoice management
      </Typography>
      
      {/* Helper text for new users */}
      <Typography variant="body2" align="center" sx={{ mb: 2, color: '#A78BFA', fontStyle: 'italic' }}>
        New here? Start your 60-day free trial — no credit card required!
      </Typography>
      
      {/* OAuth Buttons - Placed at the top like in the old design */}
      <Box sx={{ mb: 3 }}>
        <SignInButton
          provider="google"
          icon={<FcGoogle size={20} />}
          text="Continue with Google"
        />
        <SignInButton
          provider="microsoft"
          icon={<FaMicrosoft size={20} style={{ color: '#ffffff' }} />}
          text="Continue with Microsoft"
        />
      </Box>
      
      {/* Collapsible Email/Password Section */}
      {!showEmailForm ? (
        <Box sx={{ textAlign: 'center' }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={() => setShowEmailForm(true)} 
            sx={{ 
              color: 'grey.400', 
              '&:hover': { color: purpleColors.light, textDecoration: 'underline' },
              cursor: 'pointer'
            }}
          >
            Or use email instead
          </Link>
        </Box>
      ) : (
        <>
          {/* Divider with "OR" text */}
          <Box sx={{ position: 'relative', my: 2 }}>
            <Divider sx={{ '&::before, &::after': { borderColor: 'rgba(255,255,255,0.2)' } }} />
            <Typography 
              variant="caption" 
              color="grey.400" 
              sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)', 
                px: 2, 
                bgcolor: '#2A2A3E',
              }}
            >
              OR
            </Typography>
          </Box>

          {/* Email/Password Form */}
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            centered 
            variant="fullWidth" 
            TabIndicatorProps={{ sx: { backgroundColor: purpleColors.main } }}
            sx={{
              mb: 1,
              '& .MuiTab-root': { 
                color: 'grey.400', // text-gray-400 for inactive tabs
                '&.Mui-selected': { color: purpleColors.light }, // text-purple-400 for active tab
              },
            }}
          >
            <Tab label="Sign In" value="signin" />
            <Tab label="Sign Up" value="signup" />
          </Tabs>
      
      {activeTab === 'signin' && (
        <Box component="form" onSubmit={handleEmailSignIn} sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>Email Address</Typography>
            <TextField
              fullWidth
              id="email-signin"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={textFieldStyles}
              InputLabelProps={{ shrink: false }}
              placeholder="your.email@example.com"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>Password</Typography>
            <TextField
              fullWidth
              name="password-signin"
              type="password"
              id="password-signin"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={textFieldStyles}
              InputLabelProps={{ shrink: false }}
              placeholder="••••••••"
              variant="outlined"
            />
          </Box>
          
          {/* Keep only this error message with our improved styling */}
          {error && (
            <Box sx={errorStyles.container}>
              <Typography variant="body2" sx={errorStyles.text}>
                {error}
              </Typography>
            </Box>
          )}
          
          <Button type="submit" fullWidth variant="contained" sx={{ ...buttonStyles, mt: 3, mb: 1 }} disabled={loading}>
            {loading ? <CircularProgress size={24} sx={{ color: purpleColors.contrastText }}/> : 'Sign In'}
          </Button>
          <Link 
            component="button" 
            variant="body2" 
            onClick={() => { setShowResetPassword(true); setError(''); setMessage(''); }} 
            sx={{ display: 'block', textAlign: 'center', color: purpleColors.light, '&:hover': { textDecoration: 'underline' } }}
          >
            Forgot password?
          </Link>
        </Box>
      )}
      {activeTab === 'signup' && (
        <Box component="form" onSubmit={handleEmailSignUp} sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>Email Address</Typography>
            <TextField
              fullWidth
              id="email-signup"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={textFieldStyles}
              InputLabelProps={{ shrink: false }}
              placeholder="your.email@example.com"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>Password (min. 6 characters)</Typography>
            <TextField
              fullWidth
              name="password-signup"
              type="password"
              id="password-signup"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={textFieldStyles}
              InputLabelProps={{ shrink: false }}
              placeholder="••••••••"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>Confirm Password</Typography>
            <TextField
              fullWidth
              name="confirm-password-signup"
              type="password"
              id="confirm-password-signup"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              sx={textFieldStyles}
              InputLabelProps={{ shrink: false }}
              placeholder="••••••••"
              variant="outlined"
            />
          </Box>
          {/* Keep only this error message at the bottom */}
          {error && (
            <Box sx={errorStyles.container}>
              <Typography variant="body2" sx={errorStyles.text}>
                {error}
              </Typography>
            </Box>
          )}
          <Button type="submit" fullWidth variant="contained" sx={{ ...buttonStyles, mt: 3, mb: 2 }} disabled={loading}>
            {loading ? <CircularProgress size={24} sx={{ color: purpleColors.contrastText }}/> : 'Create Account'}
          </Button>
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default LoginForm;
